import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

import './ActivationTrendChart.css';

const WEEKDAY_COLOR = '#74D5F2';
const WEEKEND_COLOR = '#FF8989';
const AVERAGE_LINE_COLOR = '#E8E8E8';
const AXIS_COLOR = '#8A8A8A';

const LABELS = {
    weekday: 'Hverdag',
    weekend: 'Lørdag/søndag',
    average: '7-dages gennemsnit',
};

const ActivationTrendChart = ({ trend, height = 260 }) => {
    const option = useMemo(() => {
        const labels = trend.map((day) => day.label);

        return {
            grid: { left: 44, right: 16, top: 40, bottom: 32 },
            legend: {
                data: [LABELS.weekday, LABELS.weekend, LABELS.average],
                top: 0,
                right: 0,
                icon: 'roundRect',
                itemWidth: 10,
                itemHeight: 10,
                textStyle: { color: AXIS_COLOR, fontFamily: 'Montserrat', fontSize: 11 },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: '#2A2A2A',
                borderWidth: 0,
                textStyle: { color: 'white', fontFamily: 'Montserrat', fontSize: 12 },
                formatter: (params) => {
                    const index = params?.[0]?.dataIndex;
                    const day = trend[index];
                    if (!day) return '';

                    const dayTypeLabel = day.isWeekend ? LABELS.weekend : LABELS.weekday;
                    return [
                        `${day.dayKey} (${dayTypeLabel})`,
                        `Aktiveringer: <b>${day.count}</b>`,
                        `${LABELS.average}: <b>${day.movingAverage}</b>`,
                    ].join('<br/>');
                },
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisTick: { show: false },
                axisLine: { lineStyle: { color: '#3A3A3A' } },
                axisLabel: {
                    color: AXIS_COLOR,
                    fontFamily: 'Montserrat',
                    fontSize: 11,
                    // Ticked weekly so a 90-day window stays readable.
                    interval: (index) => Boolean(trend[index]?.isWeekStart),
                },
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                axisLabel: { color: AXIS_COLOR, fontFamily: 'Montserrat', fontSize: 11 },
                splitLine: { lineStyle: { color: '#2C2C2C' } },
            },
            series: [
                {
                    // Weekday and weekend bars are separate series purely so the legend can
                    // name them; they never occupy the same day.
                    name: LABELS.weekday,
                    type: 'bar',
                    stack: 'activations',
                    barCategoryGap: '20%',
                    itemStyle: { color: WEEKDAY_COLOR, borderRadius: [2, 2, 0, 0] },
                    data: trend.map((day) => (day.isWeekend ? null : day.count)),
                },
                {
                    name: LABELS.weekend,
                    type: 'bar',
                    stack: 'activations',
                    itemStyle: { color: WEEKEND_COLOR, borderRadius: [2, 2, 0, 0] },
                    data: trend.map((day) => (day.isWeekend ? day.count : null)),
                },
                {
                    name: LABELS.average,
                    type: 'line',
                    symbol: 'none',
                    smooth: false,
                    lineStyle: { color: AVERAGE_LINE_COLOR, width: 2 },
                    itemStyle: { color: AVERAGE_LINE_COLOR },
                    z: 3,
                    data: trend.map((day) => day.movingAverage),
                },
            ],
        };
    }, [trend]);

    return (
        <div className="ActivationTrendChart">
            <ReactECharts
                option={option}
                style={{ height, width: '100%' }}
                notMerge
                opts={{ renderer: 'svg' }}
            />
        </div>
    );
};

export default ActivationTrendChart;
